import { debounce, get } from "lodash-es";
import { defineAsyncComponent, watch } from "vue";
import Block from "@/editor/block";
import BlockEditor from "@/editor/blockEditor";

export default abstract class PageBlock extends Block {
  protected _active: boolean;

  private _props: Record<string, any>;
  private _attrs: Record<string, any>;
  private readonly debounceValidate: Function | null;

  protected constructor(json: any, editor: BlockEditor) {
    super(json, editor);

    this._active = get(json, "active", true);
    this._props = Object.assign({}, get(json, "props", {}));
    this._attrs = Object.assign({}, get(json, "attrs", {}));

    if (json.is_new) {
      this._props = Object.assign({}, get(this.init(), "props", {}));
      this._attrs = Object.assign({}, get(this.init(), "attrs", {}));
    }

    if (editor?.editable) {
      this.debounceValidate = debounce(() => this.validate(), 1000);
      this.setupWatchers();
    } else {
      this.debounceValidate = null;
    }
  }

  get props(): Record<string, any> {
    return this._props;
  }

  set props(value: Record<string, any>) {
    this._props = value;
  }

  get attrs(): Record<string, any> {
    return this._attrs;
  }

  set attrs(value: Record<string, any>) {
    this._attrs = value;
  }

  private setupWatchers(): void {
    watch(
      () => [this.props, this.attrs],
      () => this.debounceValidate && this.debounceValidate(),
      { deep: true },
    );
  }

  get active(): boolean {
    return this._active;
  }

  set active(value: boolean) {
    this._active = value;
  }

  public getEditComponent(): Promise<any> {
    if (!this._editComponent) {
      this._editComponent = defineAsyncComponent(() => import(`../Blocks/${this.type}/Edit.vue`));
    }

    return this._editComponent;
  }

  public getRenderComponent(): Promise<any> {
    if (!this._renderComponent) {
      this._renderComponent = defineAsyncComponent(() => import(`../Blocks/${this.type}/Render.vue`));
    }

    return this._renderComponent;
  }

  setActive(value: boolean): void {
    this._active = value;
    this.editor.emit("updated", this);
  }

  toJSON(): any {
    return {
      id: this.id,
      type: this.type,
      active: this.active,
      props: this.props,
      attrs: this.attrs,
    };
  }
}
